/* eslint-disable  @typescript-eslint/no-explicit-any */
import * as React from "react";
import { useContext, useEffect, useState } from "react";
import {
  Alert,
  AlertColor,
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { AppContext } from "../../utils/AppContext";
import { LoadingOverlay } from "../../components/LoadingOverlay";
import { IDataEntity, IDataStructure } from "../../services/backend/DataSchemaService";
import { useDataPersistenceService } from "../../services/backend/DataService";
import { getFormComponent, hasFormComponent } from "./Forms";
import { useCustomerSupportService } from "services/backend/CustomerSupportService";

const PlayerDataPropertiesMap = new Map<string, string>([
  ["Wallet", "walletId"],
  ["PlayerPrestige", "playerPrestigeId"],
]);

const ChangePlayerData = () => {
  const dataService = useDataPersistenceService();
  const customerSupportService = useCustomerSupportService();
  const context = useContext(AppContext);

  const [alertMessage, setAlertMessage] = useState<string | undefined>(undefined);
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("info");
  const [loading, setLoading] = useState(false);

  // Player selection
  const [playerId, setPlayerId] = useState<string>("");

  // Data structures
  const [dataStructures, setDataStructures] = useState<IDataStructure[]>([]);
  const [selectedDataStructure, setSelectedDataStructure] = useState<string>("");

  // Generic data holder
  const [entityData, setEntityData] = useState<any | undefined>(undefined);

  const [entitiesIds, setEntitiesIds] = useState<Map<string, string[]>>(new Map());
  const [inputIdValue, setInputIdValue] = useState("");
  const CONFLICT_ERROR_CODE = 269025294 | 0x1009000e;

  useEffect(() => {
    if (context) {
      context.setTitle("Change Player Data");
      context.setTargetAPI("environment");
      if (context.selectedEnvironment) {
        loadDataEntitiesIds("Player");
        fetchDataStructures();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context]);

  const loadDataEntitiesIds = async (structureName: string) => {
    try {
      if (structureName) {
        setPlayerId("");
        setInputIdValue("");
        setEntityData(undefined);
        setLoading(true);
        const { list } = await dataService.listDataEntitiesId(structureName);
        setEntitiesIds(entitiesIds.set(structureName, list));
      }
    } catch (e: any) {
      setEntitiesIds(new Map());
      setAlertMessage(e.debugMessage || "Could not load data entities ids");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const fetchDataStructures = async () => {
    try {
      setLoading(true);
      const { list } = await dataService.listDataStructures();
      // Filter only non-archived with registered forms
      setDataStructures(list.filter((ds: IDataStructure) => !ds.archived && hasFormComponent(ds.name)));
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not fetch the list of data structures.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const handleDataStructureChange = async (event: SelectChangeEvent<string>) => {
    const structureName = event.target.value;
    setSelectedDataStructure(structureName);
    setEntityData(undefined);
  };

  const handleLoadPlayerData = async () => {
    if (!playerId) {
      setAlertMessage("Please select a player.");
      setAlertSeverity("warning");
      return;
    }

    if (!selectedDataStructure) {
      setAlertMessage("Please select a data structure.");
      setAlertSeverity("warning");
      return;
    }

    if (!hasFormComponent(selectedDataStructure)) {
      setAlertMessage(`No form available for ${selectedDataStructure} data structure.`);
      setAlertSeverity("warning");
      return;
    }

    try {
      setLoading(true);
      const playerData = await dataService.loadDataEntity("Player", playerId);

      const entityId = playerData[PlayerDataPropertiesMap.get(selectedDataStructure) || ""] || "";

      const dsData = await dataService.loadDataEntity(selectedDataStructure, entityId);
      setEntityData(dsData);
      setAlertMessage(`${selectedDataStructure} data loaded successfully.`);
      setAlertSeverity("success");
    } catch (e: any) {
      setAlertMessage(e.debugMessage || `Could not load ${selectedDataStructure} data.`);
      setAlertSeverity("error");
      setEntityData(undefined);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePlayerData = async (data: IDataEntity): Promise<boolean> => {
    if (!selectedDataStructure) {
      setAlertMessage("Please select a data structure.");
      setAlertSeverity("warning");
      return false;
    }

    try {
      setLoading(true);
      await customerSupportService.updatePlayerData(playerId, selectedDataStructure, data);
      setAlertMessage(`${selectedDataStructure} data saved successfully.`);
      setAlertSeverity("success");
      setEntityData(data);
    } catch (e: any) {
      console.error(e);
      //Data version conflict
      if (e.errorCode === CONFLICT_ERROR_CODE) {
        setAlertMessage(`Data version conflict. Please reload the ${selectedDataStructure} data and try again.`);
        setAlertSeverity("warning");
        return false;
      }
      setAlertMessage(e.debugMessage || `Could not save ${selectedDataStructure} data.`);
      setAlertSeverity("error");
      return false;
    } finally {
      setLoading(false);
    }
    return true;
  };

  const handleCancel = () => {
    setEntityData(undefined);
    setSelectedDataStructure("");
    setPlayerId("");
    setInputIdValue("");
  };

  const alertCloseHandler = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertMessage(undefined);
  };

  const FormComponent = selectedDataStructure ? getFormComponent(selectedDataStructure) : null;

  return (
    <>
      <Box sx={{ width: "100%", p: 3 }}>
        <Paper sx={{ width: "100%", mb: 2, p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Player Data Management
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
            Select a player and data structure to view and edit data.
          </Typography>

          <Grid container spacing={3}>
            {/* Player ID Input */}
            <Grid size={6}>
              <Autocomplete
                value={playerId}
                id="data-entity-id"
                freeSolo
                options={entitiesIds.get("Player") || []}
                renderInput={(params) => <TextField {...params} label="Player ID" sx={{ minWidth: "240px" }} />}
                onChange={(_event, newValue: string | null) => {
                  setPlayerId(newValue ?? "");
                  if (!newValue) {
                    setEntityData(undefined);
                    setInputIdValue("");
                  }
                }}
                inputValue={inputIdValue}
                onInputChange={(_event, newInputValue) => setInputIdValue(newInputValue)}
              />
            </Grid>

            {/* Data Structure Selection */}
            <Grid size={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="data-structure-label">Data Structure</InputLabel>
                <Select
                  labelId="data-structure-label"
                  id="data-structure-select"
                  value={selectedDataStructure}
                  onChange={handleDataStructureChange}
                  label="Data Structure"
                >
                  <MenuItem value="">
                    <em>Select a data structure</em>
                  </MenuItem>
                  {dataStructures.map((structure) => (
                    <MenuItem key={structure.name} value={structure.name}>
                      {structure.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Load Button */}
            <Grid size={12}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleLoadPlayerData}
                disabled={!playerId || !selectedDataStructure}
              >
                Load Player Data
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Dynamic Form Section */}
        {entityData && FormComponent && (
          <FormComponent playerId={playerId} data={entityData} onSave={handleSavePlayerData} onCancel={handleCancel} />
        )}

        {/* Empty State */}
        {!entityData && selectedDataStructure && playerId && (
          <Paper sx={{ width: "100%", p: 5, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary">
              Click Load Player Data to start editing
            </Typography>
          </Paper>
        )}
      </Box>

      <Snackbar open={alertMessage !== undefined} autoHideDuration={5000} onClose={alertCloseHandler}>
        <Alert onClose={alertCloseHandler} severity={alertSeverity} sx={{ width: "100%" }}>
          {alertMessage}
        </Alert>
      </Snackbar>
      {loading && <LoadingOverlay />}
    </>
  );
};

export default ChangePlayerData;
